/**
 * LegCard Component
 *
 * Individual leg display in parlay results
 * Shows player, line, reasoning, and actions
 */

import React, { useState } from 'react';

const SPORT_EMOJI = {
  nba: '🏀',
  nfl: '🏈',
  nhl: '🏒',
  mlb: '⚾',
  ncaa_f: '🏈'
};

export default function LegCard({
  leg,
  legNumber,
  onSwap,
  onRemove,
  isOnlyLeg
}) {
  const [showSwapOptions, setShowSwapOptions] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);

  const handleSwap = async (alternative) => {
    setIsSwapping(true);
    try {
      await onSwap(alternative);
      setShowSwapOptions(false);
    } finally {
      setIsSwapping(false);
    }
  };

  const sportEmoji = SPORT_EMOJI[leg.sport] || '🎯';

  return (
    <div className="glass-card p-4 border-l-4 border-l-accent-blue">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{sportEmoji}</span>
          <span className="text-xs font-mono text-text-tertiary uppercase">
            LEG {legNumber}
          </span>
        </div>
        <div className="text-sm font-mono text-text-secondary">
          ({leg.odds > 0 ? '+' : ''}{leg.odds})
        </div>
      </div>

      {/* Main content */}
      <div className="mb-4">
        <h3 className="text-lg font-bold text-text-primary mb-1">
          {leg.player_name} {leg.direction} {leg.line} {leg.stat_type.toUpperCase()}
        </h3>
        <p className="text-sm text-text-secondary">
          {leg.team_name} vs {leg.opponent_name}
        </p>
      </div>

      {/* Knewton's reasoning */}
      <div className="bg-bg-card/50 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <span className="text-sm">📊</span>
          <div className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">Why this line?</span>
            <ul className="mt-1 space-y-1">
              {leg.reasoning?.map((reason, idx) => (
                <li key={idx} className="flex items-start gap-1">
                  <span>{reason.icon || '•'}</span>
                  <span>{reason.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <div className="flex items-center gap-1">
          <span className="text-text-secondary">🎯 Confidence:</span>
          <span className={`font-bold ${
            leg.confidence >= 80 ? 'text-accent-green' :
            leg.confidence >= 70 ? 'text-accent-blue' :
            'text-yellow-500'
          }`}>
            {leg.confidence}%
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-text-secondary">Hit rate:</span>
          <span className="font-medium text-text-primary">
            {leg.hit_count}/{leg.games_count} L{leg.games_count}
          </span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => {}}
          className="flex-1 py-2 px-3 rounded-lg bg-accent-green/10 text-accent-green text-sm font-medium hover:bg-accent-green/20 transition-colors"
        >
          ✅ Keep
        </button>
        <button
          onClick={() => setShowSwapOptions(!showSwapOptions)}
          className="flex-1 py-2 px-3 rounded-lg bg-accent-blue/10 text-accent-blue text-sm font-medium hover:bg-accent-blue/20 transition-colors"
        >
          🔄 Swap
        </button>
        <button
          onClick={onRemove}
          disabled={isOnlyLeg}
          className={`
            flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors
            ${isOnlyLeg
              ? 'bg-bg-card text-text-tertiary cursor-not-allowed'
              : 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
            }
          `}
        >
          ❌ Remove
        </button>
      </div>

      {/* Swap options panel */}
      {showSwapOptions && leg.alternatives && (
        <div className="mt-4 pt-4 border-t border-border">
          <h4 className="text-sm font-medium text-text-primary mb-3">
            🔄 Alternative suggestions:
          </h4>

          {/* Same player, different line */}
          {leg.alternatives.same_player?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-text-tertiary mb-2">Same player, different line:</p>
              <div className="space-y-2">
                {leg.alternatives.same_player.map((alt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSwap(alt)}
                    disabled={isSwapping}
                    className="w-full p-2 rounded-lg bg-bg-card hover:bg-bg-card-hover text-left text-sm transition-colors"
                  >
                    <span className="text-text-primary">
                      {leg.player_name} {alt.direction} {alt.line} {alt.stat_type.toUpperCase()}
                    </span>
                    <span className="text-text-secondary ml-2">
                      ({alt.odds > 0 ? '+' : ''}{alt.odds})
                    </span>
                    {alt.tag && (
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                        alt.tag === 'safer' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        {alt.tag === 'safer' ? '🐢' : '🔥'} {alt.tag}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Same game, different player */}
          {leg.alternatives.same_game?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-text-tertiary mb-2">Same game, different player:</p>
              <div className="space-y-2">
                {leg.alternatives.same_game.map((alt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSwap(alt)}
                    disabled={isSwapping}
                    className="w-full p-2 rounded-lg bg-bg-card hover:bg-bg-card-hover text-left text-sm transition-colors"
                  >
                    <span className="text-text-primary">
                      {alt.player_name} {alt.direction} {alt.line} {alt.stat_type.toUpperCase()}
                    </span>
                    <span className="text-text-secondary ml-2">
                      ({alt.confidence}% conf)
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Different game */}
          {leg.alternatives.different_game?.length > 0 && (
            <div>
              <p className="text-xs text-text-tertiary mb-2">Different game entirely:</p>
              <div className="space-y-2">
                {leg.alternatives.different_game.map((alt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSwap(alt)}
                    disabled={isSwapping}
                    className="w-full p-2 rounded-lg bg-bg-card hover:bg-bg-card-hover text-left text-sm transition-colors"
                  >
                    <span className="text-text-primary">
                      {alt.player_name} {alt.direction} {alt.line} {alt.stat_type.toUpperCase()}
                    </span>
                    <span className="text-text-secondary ml-2">
                      vs {alt.opponent_name} ({alt.confidence}%)
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => setShowSwapOptions(false)}
            className="mt-3 w-full py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
